import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CreateAfasModuleDocument } from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import {
    AfasWebhookModuleForm,
    IAfasWebhookModuleFormProps,
    IAfasType,
} from 'training/components/forms/AfasWebhookModuleForm';
import { PageTitle } from 'common/components/PageTitle';
import { IModuleCreateDrawerProps } from 'training/types';
import { gotoTrainingPage } from 'training/utils/gotoRoutes';
import { createModuleInCache } from 'training/utils/module';
import { getUrl } from 'route/utils/getUrl';

export const AfasWebhookModuleCreateDrawer = ({
    trainingId,
    moduleGroupId,
}: IModuleCreateDrawerProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const navigate = useNavigate();
    const { showApolloError } = useApolloError();

    const [createModule] = useMutation(CreateAfasModuleDocument);

    const handleSubmit: IAfasWebhookModuleFormProps['onSubmit'] = async (
        values
    ) => {
        const { afasType, ...otherValues } = values;

        const moduleValues = {
            ...otherValues,
            updateAfasSubscription: afasType === IAfasType.Subscription,
            updateAfasCourse: afasType === IAfasType.Course,
        };

        // When moduleGroupId is given we use that for creating the module inside the moduleGroup, otherwise create it
        // in the training which will make a new moduleGroup
        const parentId = moduleGroupId ? { moduleGroupId } : { trainingId };

        try {
            await createModule({
                variables: { module: moduleValues, ...parentId },
                update: (cache, { data }) => {
                    const { module: newModule, moduleGroup } =
                        data?.createAfasModule || {};

                    if (!newModule || !moduleGroup) return;

                    createModuleInCache(
                        newModule,
                        moduleGroup,
                        !moduleGroupId, // Is new module group
                        trainingId,
                        cache
                    );
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.afasWebhook.create'), {
            variant: 'success',
        });

        // Go back to training to close drawer
        gotoTrainingPage(navigate, trainingId);

        return;
    };

    return (
        <>
            <PageTitle>{translate('newAfasWebhookModule')}</PageTitle>

            <AfasWebhookModuleForm
                moduleGroupId={moduleGroupId}
                onClose={() => {
                    const routeName = moduleGroupId
                        ? 'TRAINING_CREATE_MODULE_IN_GROUP'
                        : 'TRAINING_CREATE_MODULE';

                    const url = getUrl(routeName, {
                        trainingId,
                        ...(moduleGroupId && { moduleGroupId }),
                    });

                    navigate(url);
                }}
                onSubmit={handleSubmit}
            />
        </>
    );
};
